import { useState, useCallback, useEffect, useMemo, forwardRef } from "react";
import {
  Modal,
  InputBase,
  CircularProgress,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  CalendarToday as CalendarIcon,
  AccessTime as AccessTimeIcon,
} from "@mui/icons-material";
import { useAtom } from "jotai";
import { debounce } from "lodash";
import {
  themeAtom,
  listsAtom,
  activeMultiDayIdAtom,
} from "../-Atoms";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../firebaseConfig";
import getJobName from "../GlobalFunctions/getJobName";
import LabelChipSmall from "./LabelChipSmall";

const { DateTime } = require("luxon");

// Utility function to format dates
const formatSearchDate = (dateString) => {
  if (!dateString) return null;
  return DateTime.fromISO(dateString)
    .setZone("Australia/Sydney")
    .toFormat("dd LLL");
};

// Simple date chip components for search results
const ShootDateChip = ({ shootDate, theme }) => {
  if (!shootDate) return null;

  const getShootDateColor = () => {
    const shootDateObj = DateTime.fromISO(shootDate).setZone("Australia/Sydney").startOf("day");
    const today = DateTime.now().setZone("Australia/Sydney").startOf("day");

    if (shootDateObj < today) {
      // Past date - gray/muted color
      return theme === "dark" ? "rgba(158, 158, 158, 0.2)" : "rgba(117, 117, 117, 0.1)";
    } else {
      // Future date - blue color
      return theme === "dark" ? "rgba(33, 150, 243, 0.2)" : "rgba(33, 150, 243, 0.1)";
    }
  };

  const getShootDateTextColor = () => {
    const shootDateObj = DateTime.fromISO(shootDate).setZone("Australia/Sydney").startOf("day");
    const today = DateTime.now().setZone("Australia/Sydney").startOf("day");

    if (shootDateObj < today) {
      // Past date - gray text
      return theme === "dark" ? "#9e9e9e" : "#757575";
    } else {
      // Future date - blue text
      return theme === "dark" ? "#64b5f6" : "#2196f3";
    }
  };

  return (
    <Chip
      icon={<CalendarIcon sx={{ fontSize: "1rem !important" }} />}
      label={formatSearchDate(shootDate)}
      size="medium"
      sx={{
        backgroundColor: getShootDateColor(),
        color: getShootDateTextColor(),
        fontWeight: 700,
        fontSize: "0.875rem",
        height: 32,
        borderRadius: 1,
        "& .MuiChip-icon": {
          fontSize: "1rem",
          color: getShootDateTextColor(),
        },
        "& .MuiChip-label": {
          fontSize: "0.875rem",
          fontWeight: 700,
        },
      }}
    />
  );
};

const SimpleDueDateChip = ({ dueDate, complete, theme }) => {
  if (!dueDate) return null;

  const getDueDateColor = () => {
    if (complete) return theme === "dark" ? "#81c784" : "#4caf50";

    const dueDateObj = DateTime.fromISO(dueDate).setZone("Australia/Sydney").startOf("day");
    const today = DateTime.now().setZone("Australia/Sydney").startOf("day");

    if (dueDateObj.equals(today)) return theme === "dark" ? "#ffb74d" : "#ff9800";
    if (dueDateObj < today) return theme === "dark" ? "#e57373" : "#f44336";
    return theme === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)";
  };

  const getTextColor = () => {
    if (complete) return theme === "dark" ? "#1b5e20" : "#ffffff";

    const dueDateObj = DateTime.fromISO(dueDate).setZone("Australia/Sydney").startOf("day");
    const today = DateTime.now().setZone("Australia/Sydney").startOf("day");

    if (dueDateObj.equals(today)) return theme === "dark" ? "#e65100" : "#ffffff";
    if (dueDateObj < today) return theme === "dark" ? "#b71c1c" : "#ffffff";
    return theme === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)";
  };

  return (
    <Chip
      icon={<AccessTimeIcon sx={{ fontSize: "1rem !important" }} />}
      label={formatSearchDate(dueDate)}
      size="medium"
      sx={{
        backgroundColor: getDueDateColor(),
        color: getTextColor(),
        fontWeight: 700,
        fontSize: "0.875rem",
        height: 32,
        borderRadius: 1,
        "& .MuiChip-icon": {
          fontSize: "1rem",
          color: getTextColor(),
        },
        "& .MuiChip-label": {
          fontSize: "0.875rem",
          fontWeight: 700,
        },
      }}
    />
  );
};

const SearchModalV2 = ({
  searchModalOpen,
  handleSearchModalClose,
  handleEventDataModalOpen,
  eventDataModalOpen = false,
  isDesktop = false,
  handleEventDataModalClose,
}) => {
  const [theme] = useAtom(themeAtom);
  const [inputValue, setInputValue] = useState(""); // For display only
  const [searchQuery, setSearchQuery] = useState(""); // For actual searching
  const [searchReturnValue, setSearchReturnValue] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showAllJobs, setShowAllJobs] = useState(true); // Default to showing all jobs
  
  // Memoize expensive style calculations - use CSS for responsive design
  const searchModalWidth = useMemo(() => {
    if (!isDesktop || !searchModalOpen) {
      return { xs: "95vw", sm: 700 }; // Responsive width object
    }
    // Desktop side-by-side: 30vw width when event modal is open (8vw margins)
    return eventDataModalOpen ? "30vw" : 700;
  }, [isDesktop, searchModalOpen, eventDataModalOpen]);
  
  const borderRadius = useMemo(() => {
    if (isDesktop && eventDataModalOpen) {
      return "10px 0px 0px 0px"; // Remove top-right and bottom-right for seamless connection
    }
    return "12px";
  }, [isDesktop, eventDataModalOpen]);

  // Memoize expensive style objects - use simple styles when typing for performance
  const paperStyles = useMemo(() => ({
    position: "relative",
    width: searchModalWidth,
    maxWidth: isDesktop && eventDataModalOpen ? "30vw" : 700,
    // Responsive height using CSS breakpoints
    maxHeight: isDesktop && eventDataModalOpen 
      ? "calc(100vh - 120px)" 
      : { xs: "calc(100vh - 120px)", sm: "calc(100vh - 200px)" },
    height: isDesktop && eventDataModalOpen 
      ? "calc(100vh - 120px)" 
      : { xs: "calc(100vh - 120px)", sm: "calc(100vh - 200px)" },
    borderRadius: borderRadius,
    overflow: "hidden",
    // CSS transitions for smooth animations
    transition: "all 0.2s ease-out",
    opacity: searchModalOpen ? 1 : 0,
    transform: searchModalOpen ? "scale(1)" : "scale(0.98)",
    // Consistent background - no longer changes during typing
    background: theme === "dark"
      ? "linear-gradient(135deg, rgba(28, 28, 28, 1.0) 0%, rgba(35, 35, 35, 1.0) 100%)"
      : "linear-gradient(135deg, rgba(255, 255, 255, 1.0) 0%, rgba(250, 250, 250, 1.0) 100%)",
    // Backdrop filter disabled for performance
    backdropFilter: "none",
    border: isDesktop && eventDataModalOpen
      ? "none"
      : theme === "dark"
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(0, 0, 0, 0.1)",
    borderRight: isDesktop && eventDataModalOpen ? "none" : undefined,
    // Consistent shadow - no longer changes during typing
    boxShadow: theme === "dark" ? "0 20px 40px rgba(0,0,0,0.4)" : "0 20px 40px rgba(0,0,0,0.15)",
    marginTop: isDesktop && eventDataModalOpen ? "20px" : undefined,
  }), [searchModalWidth, isDesktop, eventDataModalOpen, borderRadius, theme, searchModalOpen]);

  const modalStyles = useMemo(() => ({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: isDesktop && eventDataModalOpen ? "flex-start" : "center",
    // Responsive padding using CSS breakpoints
    pt: isDesktop && eventDataModalOpen ? "20px" : { xs: 2, sm: 8 },
    pb: 4,
    pl: isDesktop && eventDataModalOpen ? "8vw" : 0,
    zIndex: 1200, // Below Dialog (1300) to allow event modal interaction
    "& > .MuiBackdrop-root": {
      // Backdrop blur disabled entirely to prevent flashing and improve performance
      backdropFilter: "none",
      backgroundColor: isDesktop && eventDataModalOpen 
        ? "rgba(0, 0, 0, 0.3)"
        : theme === "dark" ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.4)",
    },
  }), [isDesktop, eventDataModalOpen, theme]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetSearchWindow = useCallback(() => {
    setInputValue("");
    setSearchQuery("");
    setSearchReturnValue([]);
    setSearchLoading(false);
    setShowAllJobs(true); // Reset toggle to default state
    // Close both modals when search modal closes
    if (isDesktop && eventDataModalOpen && handleEventDataModalClose) {
      handleEventDataModalClose();
    }
    handleSearchModalClose();
  }, [isDesktop, eventDataModalOpen, handleEventDataModalClose, handleSearchModalClose]);

  // Firebase search logic - debounced search trigger
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchQuery(value); // This triggers the actual search
    }, 500),
    []
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchFirebase = useCallback(
    debounce(async (searchValue, showAll) => {
      let resArray = [];
      if (parseInt(searchValue)) {
        const jobQuery = query(
          collection(db, "JobData"),
          where("year", "==", parseInt(`20${searchValue.slice(0, 2)}`)),
          where("jobNumber", "==", parseInt(searchValue))
        );
        const res = await getDocs(jobQuery).catch((err) => {
          console.log(err);
          setSearchReturnValue([]);
          setSearchLoading(false);
        });
        res.docs.forEach((doc) => {
          const data = doc.data();
          console.log(data);
          data.id = doc?.id || "";
          data.kind = "job";
          resArray.push(data);
        });
      } else {
        const clientQuery = query(
          collection(db, "ClientDatabase"),
          where(
            "brandLowerCaseArray",
            "array-contains",
            searchValue.toLowerCase()
          )
        );
        const res = await getDocs(clientQuery).catch((err) => {
          console.log(err);
          setSearchReturnValue([]);
          setSearchLoading(false);
        });
        res.docs.forEach((doc) => {
          const data = doc.data();
          data.id = doc?.id || "";
          data.kind = "client";
          resArray.push(data);
        });

        const jobQuery = query(
          collection(db, "JobData"),
          where(
            "clientLowerCaseArray",
            "array-contains",
            searchValue.toLowerCase()
          ),
          orderBy("start", "desc")
        );
        const res2 = await getDocs(jobQuery).catch((err) => {
          console.log(err);
          setSearchReturnValue([]);
          setSearchLoading(false);
        });
        res2.docs.forEach((doc) => {
          const data = doc.data();
          data.id = doc?.id || "";
          data.kind = "job";
          if (showAll || data.jobNumber) {
            resArray.push(data);
          }
        });
      }
      setSearchReturnValue(resArray);
      setSearchLoading(false);
    }, 1000),
    [setSearchReturnValue, setSearchLoading]
  );

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Escape" && searchModalOpen) {
        e.preventDefault();
        resetSearchWindow();
      }
    };

    if (searchModalOpen) {
      document.addEventListener("keydown", handleKeyPress);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [searchModalOpen, resetSearchWindow]);

  // Effect to trigger search when searchQuery or showAllJobs changes
  useEffect(() => {
    if (searchQuery !== "") {
      setSearchLoading(true);
      searchFirebase(searchQuery, showAllJobs);
    } else {
      setSearchLoading(false);
      setSearchReturnValue([]);
    }
  }, [searchQuery, showAllJobs, searchFirebase]);

  // Effect to override z-index of popup components when modal is open
  useEffect(() => {
    if (searchModalOpen) {
      // Create style element to override popup z-indexes
      const styleElement = document.createElement('style');
      styleElement.id = 'search-modal-z-index-overrides';
      styleElement.textContent = `
        .MuiPopover-root,
        .MuiMenu-root,
        .MuiDialog-root,
        .MuiTooltip-popper,
        .MuiAutocomplete-popper,
        .MuiPickersPopper-root,
        .MuiDrawer-root {
          z-index: 100000 !important;
        }
      `;
      document.head.appendChild(styleElement);

      return () => {
        // Cleanup on unmount or modal close
        const existingStyle = document.getElementById('search-modal-z-index-overrides');
        if (existingStyle) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, [searchModalOpen]);



  // Create a wrapper component that properly forwards refs
  const ModalContent = forwardRef(({ children, ...props }, ref) => (
    <div ref={ref} {...props}>
      {children}
    </div>
  ));

  return (
    <Modal
      open={searchModalOpen}
      onClose={resetSearchWindow}
      sx={modalStyles}
    >
      <ModalContent>
        <Paper
          elevation={0}
          sx={paperStyles}
        >
              {/* Header */}
              <Paper
                elevation={0}
                sx={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1000,
                  backgroundColor:
                    theme === "dark"
                      ? "rgba(28, 28, 28, 0.95)"
                      : "rgba(250, 250, 250, 0.95)",
                  backdropFilter: "blur(10px)",
                  borderBottom:
                    theme === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.1)"
                      : "1px solid rgba(0, 0, 0, 0.1)",
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <SearchIcon
                  sx={{
                    fontSize: 28,
                    color: theme === "dark" ? "#90caf9" : "#1976d2",
                  }}
                />
                <InputBase
                  autoComplete="off"
                  placeholder="Search jobs and clients..."
                  autoFocus
                  value={inputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputValue(value); // Update input immediately for responsiveness
                    debouncedSearch(value); // Debounced search trigger
                  }}
                  sx={{
                    flex: 1,
                    fontSize: "1.1rem",
                    color: theme === "dark" ? "#ffffff" : "#000000",
                    "& input::placeholder": {
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.6)",
                      opacity: 1,
                    },
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showAllJobs}
                        onChange={(e) => setShowAllJobs(e.target.checked)}
                        size="small"
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: theme === "dark" ? "#90caf9" : "#1976d2",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: theme === "dark" ? "#90caf9" : "#1976d2",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: theme === "dark" ? "#ffffff" : "#666666",
                          fontWeight: 500,
                        }}
                      >
                        Show all jobs
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                  <Chip
                    label="⌘F"
                    size="small"
                    sx={{
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.1)"
                          : "rgba(0, 0, 0, 0.1)",
                      color: theme === "dark" ? "#ffffff" : "#666666",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                    }}
                  />
                  <IconButton
                    onClick={resetSearchWindow}
                    size="small"
                    sx={{
                      color: theme === "dark" ? "#ffffff" : "#666666",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.1)",
                      },
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Paper>

              {/* Content */}
              <Box
                sx={{
                  height: "calc(100% - 80px)",
                  overflow: "hidden",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Search Results - Always present, takes full available space */}
                <Box
                  sx={{
                    flex: 1,
                    overflowY: "auto",
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {searchReturnValue.map((result, index) => (
                    <SearchResultV2
                      key={`search-result-${result.id}-${index}`}
                      result={result}
                      handleEventDataModalOpen={handleEventDataModalOpen}
                    />
                  ))}
                </Box>

                {/* Loading State - Overlay, positioned absolutely */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    visibility: searchLoading ? "visible" : "hidden",
                    opacity: searchLoading ? 1 : 0,
                    transition: "opacity 0.2s ease-in-out",
                    backgroundColor: theme === "dark" ? "rgba(28, 28, 28, 0.95)" : "rgba(255, 255, 255, 0.95)",
                    zIndex: 10,
                  }}
                >
                  <CircularProgress
                    size={60}
                    sx={{
                      color: theme === "dark" ? "#90caf9" : "#1976d2",
                    }}
                  />
                </Box>

                {/* No Results State - Overlay, positioned absolutely */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    visibility: !searchLoading && searchReturnValue.length === 0 && searchQuery !== "" ? "visible" : "hidden",
                    opacity: !searchLoading && searchReturnValue.length === 0 && searchQuery !== "" ? 1 : 0,
                    transition: "opacity 0.2s ease-in-out",
                    backgroundColor: theme === "dark" ? "rgba(28, 28, 28, 0.95)" : "rgba(255, 255, 255, 0.95)",
                    zIndex: 5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    No results found for "{searchQuery}"
                  </Typography>
                </Box>

                {/* Empty State - Overlay, positioned absolutely */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                    gap: 2,
                    visibility: !searchLoading && inputValue === "" ? "visible" : "hidden",
                    opacity: !searchLoading && inputValue === "" ? 1 : 0,
                    transition: "opacity 0.2s ease-in-out",
                    backgroundColor: theme === "dark" ? "rgba(28, 28, 28, 0.95)" : "rgba(255, 255, 255, 0.95)",
                    zIndex: 5,
                  }}
                >
                  <SearchIcon
                    sx={{
                      fontSize: 48,
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.3)"
                          : "rgba(0, 0, 0, 0.3)",
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.7)"
                          : "rgba(0, 0, 0, 0.6)",
                      textAlign: "center",
                    }}
                  >
                    Search for jobs and clients
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color:
                        theme === "dark"
                          ? "rgba(255, 255, 255, 0.5)"
                          : "rgba(0, 0, 0, 0.4)",
                      textAlign: "center",
                    }}
                  >
                    Enter a job number or client name to get started
                  </Typography>
                </Box>
              </Box>
        </Paper>
      </ModalContent>
    </Modal>
  );
};

const SearchResultV2 = ({
  result,
  handleEventDataModalOpen,
}) => {
  const [theme] = useAtom(themeAtom);
  const [lists] = useAtom(listsAtom);
  const [, setActiveMultiDayId] = useAtom(activeMultiDayIdAtom);

  const findList = (jobData) => {
    let res;
    lists.forEach((list) => {
      list.items.forEach((item) => {
        if (item === jobData.id) {
          res = list.name;
        }
      });
    });
    return res;
  };

  const handleClick = () => {
    // Clear stale activeMultiDayId to prevent opening wrong job
    setActiveMultiDayId(null);
    // Don't close search modal - let it stay open behind the EventDataModal

    // Open EventDataModalV2 with appropriate mode
    if (result.kind === "client") {
      handleEventDataModalOpen(result, true); // client-only mode
    } else {
      handleEventDataModalOpen(result, false); // normal job mode
    }
  };

  const isClient = result.kind === "client";
  const jobName = isClient ? result.brand : getJobName(result);
  const listName = !isClient ? findList(result) : null;

  return (
    <Paper
        elevation={0}
        onClick={handleClick}
        sx={{
          p: 2,
          cursor: "pointer",
          borderRadius: 2,
          background:
            theme === "dark"
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
          border:
            theme === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            background:
              theme === "dark"
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.06)",
            transform: "translateY(-1px)",
            boxShadow:
              theme === "dark"
                ? "0 4px 12px rgba(0,0,0,0.3)"
                : "0 4px 12px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: isClient
                ? theme === "dark"
                  ? "rgba(76, 175, 80, 0.2)"
                  : "rgba(76, 175, 80, 0.1)"
                : theme === "dark"
                ? "rgba(33, 150, 243, 0.2)"
                : "rgba(33, 150, 243, 0.1)",
            }}
          >
            {isClient ? (
              <BusinessIcon
                sx={{
                  color: theme === "dark" ? "#81c784" : "#4caf50",
                  fontSize: 20,
                }}
              />
            ) : (
              <WorkIcon
                sx={{
                  color: theme === "dark" ? "#64b5f6" : "#2196f3",
                  fontSize: 20,
                }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                color: theme === "dark" ? "#ffffff" : "#000000",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {jobName}
            </Typography>

            {!isClient && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 0.5 }}>
                {/* Job info row */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {listName && (
                    <Chip
                      label={listName}
                      size="small"
                      sx={{
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(156, 39, 176, 0.2)"
                            : "rgba(156, 39, 176, 0.1)",
                        color: theme === "dark" ? "#ba68c8" : "#7b1fa2",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>

                {/* Labels row */}
                {(result.idLabels || result.idLabelsPermanent) && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {result.idLabelsPermanent &&
                      result.idLabelsPermanent.map((label, index) => (
                        <LabelChipSmall
                          key={`permanent-${label}-${index}`}
                          label={label}
                          setLabelMenuOpen={() => {}}
                          kanbanBoard={false}
                        />
                      ))}
                    {result.idLabels &&
                      result.idLabels.map((label, index) => (
                        <LabelChipSmall
                          key={`regular-${label}-${index}`}
                          label={label}
                          setLabelMenuOpen={() => {}}
                          kanbanBoard={false}
                        />
                      ))}
                  </Box>
                )}

                {/* Date row - shoot date first, then due date */}
                {(result.start || result.dueDate) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <ShootDateChip shootDate={result.start} theme={theme} />
                    <SimpleDueDateChip
                      dueDate={result.dueDate}
                      complete={result.complete}
                      theme={theme}
                    />
                  </Box>
                )}
              </Box>
            )}

            {isClient && result.address && (
              <Typography
                variant="body2"
                sx={{
                  color:
                    theme === "dark"
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.6)",
                  mt: 0.5,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {result.address}
              </Typography>
            )}
          </Box>

          <Chip
            label={isClient ? "Client" : "Job"}
            size="small"
            sx={{
              backgroundColor: isClient
                ? theme === "dark"
                  ? "rgba(76, 175, 80, 0.2)"
                  : "rgba(76, 175, 80, 0.1)"
                : theme === "dark"
                ? "rgba(33, 150, 243, 0.2)"
                : "rgba(33, 150, 243, 0.1)",
              color: isClient
                ? theme === "dark"
                  ? "#81c784"
                  : "#4caf50"
                : theme === "dark"
                ? "#64b5f6"
                : "#2196f3",
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />
        </Box>
      </Paper>
  );
};

export default SearchModalV2;
